import type { CompanyResponse } from '@shared/schemas';
import { all, call, delay, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { TeamMember } from '../../_shared/domain/company-detail.model';
import type { InstitutionalDetailGateway } from '../infrastructure/institutional-detail.gateway';
import { enrichRequested, loadRequested, teamRequested } from './institutional-detail.actions';
import { selectCompany } from './institutional-detail.selectors';
import { institutionalDetailActions } from './institutional-detail.slice';

const ENRICH_ERROR = 'Enrichment failed';

/** Poll cadence for the enrichment completion check; mirrors the original page. */
const POLL_INTERVAL_MS = 3000;
/** Give up after this many polls (~60s, matching the original page's timeout). */
const POLL_MAX_ATTEMPTS = 20;

/** Best-effort human message for the enrichment failure snackbar. */
function toMessage(e: unknown): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : ENRICH_ERROR;
}

/** WORKER: load the company by id. Keeps the current row on a re-load. */
function* loadWorker(action: ReturnType<typeof loadRequested>) {
  const gateway: InstitutionalDetailGateway = yield getContext('institutionalDetailGateway');
  yield put(institutionalDetailActions.loadStarted());
  try {
    const company = (yield call(gateway.loadCompany, action.payload)) as CompanyResponse;
    yield put(institutionalDetailActions.loadSucceeded(company));
  } catch {
    yield put(institutionalDetailActions.loadFailed());
  }
}

/** WORKER: lazily load the team when the Team tab is first opened. */
function* teamWorker(action: ReturnType<typeof teamRequested>) {
  const gateway: InstitutionalDetailGateway = yield getContext('institutionalDetailGateway');
  yield put(institutionalDetailActions.teamLoadStarted());
  try {
    const team = (yield call(gateway.loadTeam, action.payload)) as TeamMember[];
    yield put(institutionalDetailActions.teamLoadSucceeded(team));
  } catch {
    yield put(institutionalDetailActions.teamLoadFailed());
  }
}

/**
 * WORKER: trigger enrichment, then poll the company until `data_updated_at`
 * changes (up to POLL_MAX_ATTEMPTS). A change patches the loaded company;
 * exhausting the poll is a (non-error) timeout that just stops the spinner.
 */
function* enrichWorker(action: ReturnType<typeof enrichRequested>) {
  const { id, force } = action.payload;
  const gateway: InstitutionalDetailGateway = yield getContext('institutionalDetailGateway');
  const previous = (yield select(selectCompany)) as CompanyResponse | null;
  const previousUpdatedAt = previous?.data_updated_at ?? null;

  yield put(institutionalDetailActions.enrichStarted());
  try {
    yield call(gateway.enrich, id, force);
    yield put(institutionalDetailActions.enrichAccepted());
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
      yield delay(POLL_INTERVAL_MS);
      const updated = (yield call(gateway.loadCompany, id)) as CompanyResponse;
      if (updated.data_updated_at !== previousUpdatedAt) {
        yield put(institutionalDetailActions.enrichSucceeded(updated));
        return;
      }
    }
    yield put(institutionalDetailActions.enrichTimedOut());
  } catch (e) {
    yield put(institutionalDetailActions.enrichFailed(toMessage(e)));
  }
}

/** WATCHERS. */
export function* institutionalDetailSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(teamRequested.type, teamWorker),
    takeLatest(enrichRequested.type, enrichWorker),
  ]);
}
